import { useCallback, useMemo, useState } from 'react';
import useFetchList from 'hooks/useFetchList';
import { Box, IconButton, Typography, makeStyles } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CheckIcon from '@material-ui/icons/Check';
import Modal from '../../../../../Shared/Modal';
import { useSelector } from 'react-redux';
import { GET_ALLOWED_ACCOUNT_COLUMNS } from '../../../../constants';
import { getLocalizedLabel } from '../../../../../../helpers/localize';
import { CUSTOM_TAGS_OPTIONS } from '../../../../../Shared/CustomTags';
import AllowedAccountsModalFooter from './AllowedAccountsModalFooter';
import AllowedAccountsList from './AllowedAccountsList';
import debounce from 'lodash/debounce';
import QhsTable from '../../../../../Shared/Table/Table';
import { useGenerateColumns } from '../../../../../Shared/Table/helpers';
import { TABLE_KEYS } from '../../../../../../redux_store';

const useStyles = makeStyles(theme => ({
  modal: {
    maxWidth: 1400,
  },
  gridContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    width: '90vw',
    maxWidth: 1200,
    height: 'calc(100vh - 400px)',
    maxHeight: 600,
  },
  tableSection: {
    flex: '1 1 65%',
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    padding: theme.spacing(2),
  },
  listSection: {
    flex: '1 1 35%',
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    padding: theme.spacing(2),
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid rgba(0, 168, 255, 0.1)`,
    flexShrink: 0,
  },
  tableWrapper: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  buttonContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButton: {
    color: theme.colors?.green || '#41C377',
    '&:hover': {
      backgroundColor: 'rgba(65, 195, 119, 0.04)',
    },
  },
  selectedButton: {
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: 'rgba(0, 168, 255, 0.04)',
    },
  },
}));

const AllowedAccountsSectionModal = ({
  values,
  initAllowedAccounts,
  changeAllowedAccounts,
  toggleAllowedAccountsModal,
  openAllowedAccountsModal,
  userUuid,
  accountUuid,
}) => {
  const {
    en: { titles },
  } = useSelector(state => state.i18n);
  const classes = useStyles();

  // Patches state -- tracks user selections within this session
  const [includedAccountsUUIDs, setIncludedAccountsUUIDs] = useState(values?.allowedAccounts ?? []);
  const [excludedAccountsUUIDs, setExcludedAccountsUUIDs] = useState(
    initAllowedAccounts?.filter(x => !values?.allowedAccounts?.includes(x)) ?? []
  );

  // Deferred patches for left table -- synced on sort/filter/loadMore
  const [tablePatches, setTablePatches] = useState({
    includedAccountsUUIDs: values?.allowedAccounts ?? [],
    excludedAccountsUUIDs:
      initAllowedAccounts?.filter(x => !values?.allowedAccounts?.includes(x)) ?? [],
  });

  // Right panel patches -- updated immediately on each click
  const [rightPanelPatches, setRightPanelPatches] = useState({
    includedAccountsUUIDs: values?.allowedAccounts ?? [],
    excludedAccountsUUIDs:
      initAllowedAccounts?.filter(x => !values?.allowedAccounts?.includes(x)) ?? [],
  });

  const tableKey = TABLE_KEYS.allowedAccountsTable;
  const columns = useGenerateColumns(
    GET_ALLOWED_ACCOUNT_COLUMNS,
    tableKey,
    { accountUuid },
    {}
  ).columns;
  const fields = columns
    .map(columnData => columnData.accessorKey)
    .sort()
    .join();

  const baseEndpoint = `/allowedAccounts/users/${userUuid}/accounts/${accountUuid}`;

  const {
    items,
    totalCount,
    sort,
    filter,
    loading: dataLoading,
    onLoadMoreRows,
    onSort,
    onFilter,
    clearFilter,
    fetchError,
  } = useFetchList({
    baseEndpoint,
    baseParams: {
      fields,
      q: undefined,
    },
    customMethod: 'POST',
    patches: {
      includedAccountsUUIDs: tablePatches.includedAccountsUUIDs,
      excludedAccountsUUIDs: tablePatches.excludedAccountsUUIDs,
    },
    tableKey,
    canLoad: fields.length,
  });

  const accounts = useMemo(() => {
    if (!items) return [];
    return items.map(account => ({
      ...account,
      customTags: account.customTags
        ? account.customTags.map(item => getLocalizedLabel(CUSTOM_TAGS_OPTIONS, item))
        : [],
    }));
  }, [items]);

  // Sync table patches on sort/filter/loadMore
  const syncTablePatches = useCallback(
    fn =>
      (...args) => {
        setTablePatches({
          includedAccountsUUIDs,
          excludedAccountsUUIDs,
        });
        fn(...args);
      },
    [includedAccountsUUIDs, excludedAccountsUUIDs]
  );

  const handleAddAccount = useCallback(
    uuid => {
      const newIncluded = [...new Set([...includedAccountsUUIDs, uuid])];
      const newExcluded = excludedAccountsUUIDs.filter(id => id !== uuid);

      setIncludedAccountsUUIDs(newIncluded);
      setExcludedAccountsUUIDs(newExcluded);
      setRightPanelPatches({
        includedAccountsUUIDs: newIncluded,
        excludedAccountsUUIDs: newExcluded,
      });
    },
    [includedAccountsUUIDs, excludedAccountsUUIDs]
  );

  const handleRemoveAccount = useCallback(
    uuid => {
      const newIncluded = includedAccountsUUIDs.filter(id => id !== uuid);
      const isOriginallyAllowed = initAllowedAccounts?.includes(uuid);
      const newExcluded = isOriginallyAllowed
        ? [...new Set([...excludedAccountsUUIDs, uuid])]
        : excludedAccountsUUIDs;

      setIncludedAccountsUUIDs(newIncluded);
      setExcludedAccountsUUIDs(newExcluded);
      setRightPanelPatches({
        includedAccountsUUIDs: newIncluded,
        excludedAccountsUUIDs: newExcluded,
      });
    },
    [includedAccountsUUIDs, excludedAccountsUUIDs, initAllowedAccounts]
  );

  const isAccountAllowed = useCallback(
    uuid => {
      if (excludedAccountsUUIDs.includes(uuid)) return false;
      if (includedAccountsUUIDs.includes(uuid)) return true;
      return false;
    },
    [includedAccountsUUIDs, excludedAccountsUUIDs]
  );

  const renderRowActions = useCallback(
    ({ row }) => {
      const account = row.original;
      const isAllowed = isAccountAllowed(account.uuid);

      return (
        <div className={classes.buttonContainer}>
          <IconButton
            className={isAllowed ? classes.selectedButton : classes.addButton}
            onClick={() =>
              isAllowed ? handleRemoveAccount(account.uuid) : handleAddAccount(account.uuid)
            }
            title={isAllowed ? 'Remove from allowed' : 'Add to allowed'}
            size="small"
          >
            {isAllowed ? <CheckIcon /> : <AddIcon />}
          </IconButton>
        </div>
      );
    },
    [classes, isAccountAllowed, handleAddAccount, handleRemoveAccount]
  );

  const closeModal = () => {
    toggleAllowedAccountsModal(false);
    // Reset to initial state
    setIncludedAccountsUUIDs(values?.allowedAccounts ?? []);
    setExcludedAccountsUUIDs(
      initAllowedAccounts?.filter(x => !values?.allowedAccounts?.includes(x)) ?? []
    );
    setTablePatches({
      includedAccountsUUIDs: values?.allowedAccounts ?? [],
      excludedAccountsUUIDs:
        initAllowedAccounts?.filter(x => !values?.allowedAccounts?.includes(x)) ?? [],
    });
    setRightPanelPatches({
      includedAccountsUUIDs: values?.allowedAccounts ?? [],
      excludedAccountsUUIDs:
        initAllowedAccounts?.filter(x => !values?.allowedAccounts?.includes(x)) ?? [],
    });
  };

  const applyChanges = () => {
    changeAllowedAccounts(includedAccountsUUIDs);
    closeModal();
    clearFilter();
  };

  const _onLoadMoreRows = syncTablePatches(onLoadMoreRows);
  const _onSort = syncTablePatches(onSort);
  const _onFilter = debounce(syncTablePatches(onFilter), 500);

  return (
    <Modal
      open={openAllowedAccountsModal}
      classes={{ paper: classes.modal }}
      title={titles.allowedAccounts}
      handleClose={closeModal}
    >
      <Box p={3} display="flex" flexDirection="column">
        <div className={classes.gridContainer}>
          <div className={classes.tableSection}>
            <Typography className={classes.sectionTitle}>
              {titles.availableAccounts || 'Available Accounts'}
            </Typography>
            <div className={classes.tableWrapper}>
              <QhsTable
                columns={columns}
                data={accounts}
                fetchNextPage={_onLoadMoreRows}
                isFetching={dataLoading}
                isLoading={(dataLoading && !items) || !openAllowedAccountsModal}
                totalRowCount={totalCount}
                sorting={sort}
                setSorting={_onSort}
                filter={filter}
                setFilters={_onFilter}
                tableKey={tableKey}
                renderRowActions={renderRowActions}
                rowActionsTitleSize={20}
                fetchError={fetchError}
              />
            </div>
          </div>

          <div className={classes.listSection}>
            <Typography className={classes.sectionTitle}>{titles.allowedAccounts}</Typography>
            <AllowedAccountsList
              userUuid={userUuid}
              accountUuid={accountUuid}
              includedAccountsUUIDs={rightPanelPatches.includedAccountsUUIDs}
              excludedAccountsUUIDs={rightPanelPatches.excludedAccountsUUIDs}
              onRemoveAccount={handleRemoveAccount}
            />
          </div>
        </div>

        <AllowedAccountsModalFooter applyChanges={applyChanges} closeModal={closeModal} />
      </Box>
    </Modal>
  );
};

export default AllowedAccountsSectionModal;
